import './AssigneeAddButton.scss'

import React from 'react'
import PropTypes from 'prop-types'

import { Icon } from 'react-fa'
import classNames from 'classnames'
import match from 'autosuggest-highlight/match'
import parse from 'autosuggest-highlight/parse'
import Autosuggest from 'react-autosuggest'
import Assignee, { getFullNameForAssignee, fromUser } from '../Assignee'
import { KEYS_ESCAPE } from '../../constants/keyboardKeys'
import differenceBy from 'lodash/differenceBy'
import sortBy from 'lodash/sortBy'
import Popover from '@material-ui/core/Popover'

const PLACEHOLDER_TEXT_KEY = 'taskBoards.Label_SearchMembers'

// https://developer.mozilla.org/en/docs/Web/JavaScript/Guide/Regular_Expressions#Using_Special_Characters
const escapeRegexCharacters = (str) => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export const mapUserToSuggestion = (user, onSelect) => ({
  id: user.id,
  value: getFullNameForAssignee(user),
  getIcon: () => <Assignee assignment={fromUser(user)} />,
  onSelect: () => onSelect(user)
})

const didSuggestionArrayChange = (current, next) => {
  return next && (next.length !== current.length || differenceBy(current, next, 'id').length > 0)
}

const getAvailableUsers = (selected, users) => {
  return sortBy(
    differenceBy(users, selected, 'id'), [
      member => member.firstName.toLowerCase(),
      member => member.lastName.toLowerCase()
    ]
  )
}

export class UserSelectMenu extends React.Component {

  constructor(props) {
    super(props)

    this.state = {
      value: '',
      suggestions: []
    }
  }

  UNSAFE_componentWillReceiveProps = (nextProps) => {
    const selectedChanged =
      didSuggestionArrayChange(this.props.selectedUsers, nextProps.selectedUsers) ||
      didSuggestionArrayChange(this.props.prioritySuggestions, nextProps.prioritySuggestions)

    if (selectedChanged) {
      this.setState({
        suggestions: this.getSuggestions('', nextProps)
      })
    }
  }

  renderSuggestion = (suggestion, { query }) => {
    const matches = match(suggestion.value, query)
    const parts = matches.length === (query.match(/\s+/g) || []).length + 1
      // default behaviour of parse is to consider 'bob smith' matching 'bob white'
      ? parse(suggestion.value, matches)
      : [{ text: suggestion.value, highlight: false }]
    const classes = [ 'react-autosuggest__suggestion-content' ].concat(suggestion.classes || [])

    return (
      <span className={classes.join(' ')}>
        { suggestion.getIcon && suggestion.getIcon() }
        <span className='react-autosuggest__suggestion-name'>
          {
            parts.map((part, index) =>
              <span
                className={classNames({ 'react-autosuggest__suggestion-highlight': part.highlight })}
                key={index}>{part.text}</span>
            )
          }
        </span>
      </span>
    )
  }

  onChange = (event, { newValue, method }) => {
    const isSelect = method === 'enter' || method === 'click'
    if (!isSelect) {
      this.setState({ value: newValue })
    }
  }

  onSuggestionSelected = (event, selectionResult) => {
    selectionResult.suggestion.onSelect()

    if (!this.props.keepOpenOnSelect) {
      this.closeAddDialog()
    }
  }

  onSuggestionsFetchRequested = ({ value }) => {
    this.setState(prevState => ({
      suggestions: this.getSuggestions(prevState.value, this.props)
    }))
  }

  getMessage(suggestions) {
    const { localize, allUsers, selectedUsers } = this.props

    if (allUsers.length === 0) {
      return localize('assignments.Label_EmptyWorkspace')
    }

    if (getAvailableUsers(selectedUsers, allUsers).length === 0) {
      return localize('taskBoards.Msg_AllMembersAssigned')
    }

    if (suggestions.length === 0) {
      return localize('assignments.Label_NoMatch')
    }
  }

  getSuggestions = (value, props) => {
    const { prioritySuggestions, onSelect, selectedUsers, allUsers } = props
    const escapedValue = escapeRegexCharacters(value.trim())
    const availableUsers = getAvailableUsers(selectedUsers, allUsers)
      .map(user => mapUserToSuggestion(user, onSelect))

    if (escapedValue === '') {
      return prioritySuggestions.concat(availableUsers)
    }

    const regex = new RegExp('\\b' + escapedValue, 'i')
    return prioritySuggestions.concat(availableUsers).filter(suggestion => regex.test(suggestion.value))
  }

  closeAddDialog = () => {
    this.props.onClose()
  }

  onBlur = () => {
    this.closeAddDialog()
  }

  getMenuPositioning() {
    const horizontal = this.props.direction === 'left' ? 'right' : 'left'
    return {
      anchorOrigin: { horizontal, vertical: 'bottom' },
      transformOrigin: { horizontal, vertical: 'top' }
    }
  }

  render() {
    const { value, suggestions } = this.state
    const { onOpen, anchor, localize } = this.props
    const message = this.getMessage(suggestions)
    const inputProps = {
      placeholder: localize(PLACEHOLDER_TEXT_KEY),
      value,
      onKeyDown: this.onKeyPress,
      onChange: this.onChange,
      onBlur: this.onBlur,
      autoFocus: true
    }
    const menuPositioning = this.getMenuPositioning()

    return (
      <Popover
        onEntered={onOpen}
        open
        anchorEl={anchor}
        anchorOrigin={menuPositioning.anchorOrigin}
        transformOrigin={menuPositioning.transformOrigin}
        onClose={this.closeAddDialog}
      >
        <Autosuggest
          highlightFirstSuggestion
          suggestions={suggestions}
          alwaysRenderSuggestions
          shouldRenderSuggestions={() => true}
          onSuggestionSelected={this.onSuggestionSelected}
          onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
          getSuggestionValue={getFullNameForAssignee}
          renderSuggestionsContainer={
            ({ containerProps, children, query }) =>
              <div {...containerProps} >
                {children}
                { message &&
                  <div className='react-autosuggest__message'>{message}</div>
                }
              </div>
          }
          renderSuggestion={this.renderSuggestion}
          inputProps={inputProps} />
      </Popover>
    )
  }
}

UserSelectMenu.defaultProps = {
  keepOpenOnSelect: false,
  selectedUsers: [],
  onOpen: () => {},
  direction: 'right',
  prioritySuggestions: []
}

UserSelectMenu.propTypes = {
  anchor: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.object
  ]),
  localize: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onOpen: PropTypes.func,
  onSelect: PropTypes.func.isRequired,
  allUsers: PropTypes.array.isRequired,
  prioritySuggestions: PropTypes.array.isRequired,
  selectedUsers: PropTypes.array,
  keepOpenOnSelect: PropTypes.bool,
  direction: PropTypes.string,
}

export class AssigneeAddButton extends React.PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      focusAfterClose: null,
      isOpening: false,
      isOpen: false,
      value: '',
      suggestions: []
    }
  }

  closeAddDialog = (e) => {
    // in filter, close is attempted while opening.
    this.setState({
      isOpening: false,
      isOpen: false,
      value: ''
    })
  }

  openUserSearch = () => {
    this.setState({
      isOpen: true,
      focusAfterClose: document.activeElement
    })
  }

  onKeyPress = (event) => {
    switch (event.key) {
      case KEYS_ESCAPE:
        this.closeAddDialog()
        event.stopPropagation()
        break
      default:
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // onclosing
    if (prevState.isOpen && !this.state.isOpen) {
      this.state.focusAfterClose.focus()
    }
  }

  onClick = (e) => {
    if (this.state.isOpening) {
      this.openUserSearch()
    }
  }

  render() {
    const buttonRef = this.refs['assignee-add-button']

    return (
      <div className={'assignee-add-container'}>
        <div
          // onMouseDown => Input onBlur => onClick, we don't want to reopen autosuggest if blur target is this button
          onMouseDown={() => { this.setState({ isOpening: true }) }}
          onClick={this.onClick}
          className={classNames({
            'assignee-add-button': true,
            'assignee-add-button--open': this.state.isOpen
          })}
          ref='assignee-add-button'>
          <Icon name='user-plus' />
        </div>
        {
          this.state.isOpen && buttonRef && <UserSelectMenu
            anchor={buttonRef}
            selectedUsers={this.props.currentAssignments}
            allUsers={this.props.projectMembers}
            prioritySuggestions={this.props.prioritySuggestions}
            localize={this.props.localize}
            onOpen={() => this.setState({ isOpening: false })}
            onSelect={this.props.onSelect}
            keepOpenOnSelect={this.props.keepOpenOnSelect}
            onClose={this.closeAddDialog}
            direction={this.props.menuDirection}
            />

          }
      </div>
    )
  }
}

AssigneeAddButton.defaultProps = {
  keepOpenOnSelect: false,
  menuDirection: 'right',
}

AssigneeAddButton.propTypes = {
  localize: PropTypes.func,
  projectMembers: PropTypes.array.isRequired,
  currentAssignments: PropTypes.array.isRequired,
  prioritySuggestions: PropTypes.array,
  onSelect: PropTypes.func.isRequired,
  keepOpenOnSelect: PropTypes.bool,
  menuDirection: PropTypes.string,
}

export default AssigneeAddButton



// WEBPACK FOOTER //
// ./src/components/AssigneeAddButton/AssigneeAddButton.js